import { optionNameFromColumn } from './toriutImportFile';
import {
  ROW_TYPE_PARENT,
  ROW_TYPE_VARIANT,
  VARIANT_IMPORT_MODE_NONE,
  VARIANT_IMPORT_MODE_WITH_VARIANTS,
} from './variantImportConstants';

/**
 * Removes client-only fields used during CSV validation.
 */
export function stripVariantRowMeta(payload) {
  const {
    parentSku,
    variantOptions,
    ...product
  } = payload;
  return product;
}

function buildVariantPayload(row) {
  const product = stripVariantRowMeta(row.payload);
  const options = (row.variantOptions || [])
    .filter(({ value }) => value !== undefined && value !== null && String(value).trim() !== '')
    .map(({ name, value }) => ({
      name: String(name),
      value: String(value),
    }));

  return {
    ...product,
    sourceRowIndex: row.rowIndex,
    options,
  };
}

function buildParentProductPayload(row) {
  return {
    ...stripVariantRowMeta(row.payload),
    sourceRowIndex: row.rowIndex,
    variants: [],
  };
}

/**
 * Builds the request body for `sdk.products.batchImport`.
 *
 * @returns {{
 *   importMode: string,
 *   layer: string,
 *   columnConfig: object,
 *   variantImport: {
 *     mode: 'none' | 'withVariants',
 *     parentSkuColumn?: string,
 *     optionColumns?: string[],
 *   },
 *   products: Array<object>,
 * }}
 *
 * `products` (mode `none`): flat list — one CSV row → one product.
 *
 * `products` (mode `withVariants`): one entry per parent row; each product may include:
 *   variants: Array<{
 *     sku: string,
 *     options: Array<{ name: string, value: string }>,
 *     sourceRowIndex: number,
 *     title?: string,
 *     meta?: object,
 *     ...
 *   }>
 */
export function buildBatchImportPayload({
  validRows,
  variantImportMode,
  parentSkuColumn,
  optionColumns,
  importMode,
  layer,
  columnConfig,
}) {
  const variantImport = variantImportMode === VARIANT_IMPORT_MODE_WITH_VARIANTS
    ? {
      mode: VARIANT_IMPORT_MODE_WITH_VARIANTS,
      parentSkuColumn,
      optionColumns: optionColumns.map(optionNameFromColumn),
    }
    : { mode: VARIANT_IMPORT_MODE_NONE };

  if (variantImportMode !== VARIANT_IMPORT_MODE_WITH_VARIANTS) {
    return {
      importMode,
      layer,
      columnConfig,
      variantImport,
      products: validRows.map((row) => ({
        ...stripVariantRowMeta(row.payload),
        sourceRowIndex: row.rowIndex,
      })),
    };
  }

  const productsBySku = new Map();

  validRows.forEach((row) => {
    if (row.rowType === ROW_TYPE_PARENT) {
      productsBySku.set(row.sku, buildParentProductPayload(row));
    }
  });

  validRows.forEach((row) => {
    if (row.rowType !== ROW_TYPE_VARIANT) {
      return;
    }
    const parent = productsBySku.get(row.parentSku);
    if (!parent) {
      return;
    }
    parent.variants.push(buildVariantPayload(row));
  });

  return {
    importMode,
    layer,
    columnConfig,
    variantImport,
    products: Array.from(productsBySku.values()),
  };
}

export function countImportPayloadStats(payload) {
  const productCount = payload.products.length;
  const variantCount = payload.products.reduce(
    (sum, product) => sum + (product.variants?.length || 0),
    0,
  );

  return {
    productCount,
    variantCount,
    totalRowCount: productCount + variantCount,
  };
}
